import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Surface, Button, Input, Badge, Modal, NewButton } from "@/components/ui-kit";
import VehicleSearchSelect from "@/components/VehicleSearchSelect";
import { useAuth } from "@/contexts/AuthContext";
import { requestOrDelete } from "@/lib/approval";
import { downloadCsv, printElement } from "@/lib/csv";
import { formatDate } from "@/lib/format";
import { GasPump, MagnifyingGlass, X } from "@phosphor-icons/react";
import ReportToolbar, { ZoomScope, ZOOM_DEFAULT } from "@/components/ReportToolbar";

const empty = {
    vehicle_id: "",
    driver_id: "",
    refuel_date: new Date().toISOString().slice(0, 10),
    odometer_reading: "",
    fuel_litres: "",
    rate_per_litre: "",
    remarks: "",
};

export default function MileageCalculation() {
    const { user } = useAuth();
    const [searchParams] = useSearchParams();
    const [logs, setLogs] = useState([]);
    const [vehicles, setVehicles] = useState([]);
    const [drivers, setDrivers] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(empty);
    const [editing, setEditing] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    const [search, setSearch] = useState("");
    const [filterVehicle, setFilterVehicle] = useState("");
    const [filterDriver, setFilterDriver] = useState("");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const tableRef = useRef(null);

    // Server-side pagination state (latest-first; page 1 loads instantly via Mongo index)
    const PAGE_SIZE = 20;
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [busy, setBusy] = useState(false);
    const [zoom, setZoom] = useState(ZOOM_DEFAULT);
    // Full filtered set, fetched on-demand for totals + CSV/PDF.
    const [allRows, setAllRows] = useState([]);
    const [allRowsLoaded, setAllRowsLoaded] = useState(false);

    // Honour deep-links from the Dashboard P&L card: ?vehicle=<id>&month=YYYY-MM
    useEffect(() => {
        const qv = searchParams.get("vehicle");
        const qm = searchParams.get("month");
        if (qv) setFilterVehicle(qv);
        if (qm && /^\d{4}-\d{2}$/.test(qm)) {
            const [y, m] = qm.split("-").map(Number);
            const last = new Date(y, m, 0).getDate();
            setDateFrom(`${qm}-01`);
            setDateTo(`${qm}-${String(last).padStart(2, "0")}`);
        }
    }, [searchParams]);

    // Build the filter portion of the query string (without page/page_size).
    const buildFilterParams = useCallback(() => {
        const params = new URLSearchParams();
        if (search.trim()) params.set("q", search.trim());
        if (filterVehicle) params.set("vehicle_id", filterVehicle);
        // driver, date range filters are still applied client-side because the
        // backend list endpoint only knows about vehicle_id + free-text search.
        return params;
    }, [search, filterVehicle]);

    const applyClientFilters = useCallback((rows) => {
        return rows.filter((l) => {
            if (filterDriver && l.driver_id !== filterDriver) return false;
            if (dateFrom && (l.refuel_date || "") < dateFrom) return false;
            if (dateTo && (l.refuel_date || "") > dateTo) return false;
            return true;
        });
    }, [filterDriver, dateFrom, dateTo]);

    const loadPage = useCallback(async (targetPage) => {
        const tp = targetPage ?? 1;
        setBusy(true);
        try {
            const params = buildFilterParams();
            params.set("page", String(tp));
            params.set("page_size", String(PAGE_SIZE));
            const r = await api.get(`/mileage?${params.toString()}`);
            const payload = r.data || {};
            setLogs(applyClientFilters(payload.items || []));
            setTotal(payload.total ?? (payload.items?.length || 0));
            setPage(payload.page ?? tp);
            setAllRowsLoaded(false);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Failed to load mileage logs");
        } finally {
            setBusy(false);
        }
    }, [buildFilterParams, applyClientFilters]);

    // Lazy-fetch the full filtered set (for totals row + CSV/PDF exports).
    const loadAll = useCallback(async () => {
        if (allRowsLoaded) return allRows;
        const params = buildFilterParams();
        const r = await api.get(`/mileage${params.toString() ? `?${params.toString()}` : ""}`);
        const rows = Array.isArray(r.data) ? r.data : (r.data?.items || []);
        const filteredAll = applyClientFilters(rows);
        setAllRows(filteredAll);
        setAllRowsLoaded(true);
        return filteredAll;
    }, [allRowsLoaded, allRows, buildFilterParams, applyClientFilters]);

    // Load lookup data once
    useEffect(() => {
        Promise.all([
            api.get("/vehicles"),
            api.get("/drivers"),
        ]).then(([v, d]) => {
            setVehicles(v.data || []);
            setDrivers(d.data || []);
        });
    }, []);

    // Refetch page when filters change (reset to page 1)
    useEffect(() => {
        setPage(1);
        loadPage(1);
    }, [search, filterVehicle, filterDriver, dateFrom, dateTo, loadPage]);

    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

    const vehMap = useMemo(() => Object.fromEntries(vehicles.map((v) => [v.id, v])), [vehicles]);
    const drvMap = useMemo(() => Object.fromEntries(drivers.map((d) => [d.id, d])), [drivers]);

    // Previous-odometer baseline for the modal.
    // - EDIT mode: always use the value persisted on this row at POST time.
    //   It is the immutable baseline for this log; we must NEVER infer it from
    //   sibling rows because the chain may have shifted around this entry.
    // - ADD mode: use the latest reading on file for the vehicle (or the
    //   vehicle's master odometer if no logs exist).
    const selectedVehicle = vehMap[form.vehicle_id];
    const prevOdoForSelection = useMemo(() => {
        if (!selectedVehicle) return null;
        if (editing) {
            const me = logs.find((l) => l.id === editing);
            if (me && me.previous_odometer != null) return me.previous_odometer;
        }
        const prior = logs
            .filter((l) => l.vehicle_id === selectedVehicle.id)
            .sort((a, b) => (b.refuel_date || "").localeCompare(a.refuel_date || "") || (b.created_at || "").localeCompare(a.created_at || ""));
        if (prior.length > 0) return prior[0].odometer_reading;
        return selectedVehicle.odometer || 0;
    }, [selectedVehicle, logs, editing]);

    const newOdoNum = form.odometer_reading === "" ? NaN : Number(form.odometer_reading);
    const fuelNum = form.fuel_litres === "" ? NaN : Number(form.fuel_litres);
    const rateNum = form.rate_per_litre === "" ? NaN : Number(form.rate_per_litre);
    const distancePreview = Number.isFinite(newOdoNum) && prevOdoForSelection != null && newOdoNum > prevOdoForSelection
        ? newOdoNum - prevOdoForSelection : null;
    const obtainedPreview = distancePreview != null && Number.isFinite(fuelNum) && fuelNum > 0
        ? +(distancePreview / fuelNum).toFixed(2) : null;
    const fixedKmpl = selectedVehicle?.fixed_mileage_kmpl ?? null;
    const expectedFuelPreview = distancePreview != null && fixedKmpl ? +(distancePreview / fixedKmpl).toFixed(2) : null;
    const fuelDeltaPreview = expectedFuelPreview != null && Number.isFinite(fuelNum) ? +(fuelNum - expectedFuelPreview).toFixed(2) : null;
    const amountDeltaPreview = fuelDeltaPreview != null && Number.isFinite(rateNum) && rateNum >= 0
        ? +(fuelDeltaPreview * rateNum).toFixed(2) : null;
    const totalCostPreview = Number.isFinite(fuelNum) && Number.isFinite(rateNum) && rateNum >= 0
        ? +(fuelNum * rateNum).toFixed(2) : null;

    // `logs` is already the current page filtered by all active filters (server + client).
    // For totals we use the lazily-loaded full filtered set; until it loads we
    // show "—" instead of misleading partial totals.
    const totals = useMemo(() => {
        if (!allRowsLoaded) return null;
        let fuelL = 0, deltaL = 0, amount = 0, distance = 0, cost = 0;
        let hasDelta = false, hasAmount = false, hasCost = false;
        allRows.forEach((l) => {
            fuelL += Number(l.fuel_litres || 0);
            distance += Number(l.distance_km || 0);
            if (l.fuel_delta_litres != null) { deltaL += Number(l.fuel_delta_litres); hasDelta = true; }
            if (l.amount_delta_inr != null) { amount += Number(l.amount_delta_inr); hasAmount = true; }
            if (l.fuel_cost_inr != null) { cost += Number(l.fuel_cost_inr); hasCost = true; }
        });
        return {
            fuel_litres: +fuelL.toFixed(2),
            distance_km: distance,
            fuel_delta_litres: hasDelta ? +deltaL.toFixed(2) : null,
            amount_delta_inr: hasAmount ? +amount.toFixed(2) : null,
            fuel_cost_inr: hasCost ? +cost.toFixed(2) : null,
        };
    }, [allRows, allRowsLoaded]);

    const hasFilters = search || filterVehicle || filterDriver || dateFrom || dateTo;
    const clearFilters = () => { setSearch(""); setFilterVehicle(""); setFilterDriver(""); setDateFrom(""); setDateTo(""); };

    const openAdd = () => { setForm(empty); setEditing(null); setOpen(true); };
    const openEdit = (l) => {
        setForm({
            vehicle_id: l.vehicle_id || "",
            driver_id: l.driver_id || "",
            refuel_date: l.refuel_date || "",
            odometer_reading: String(l.odometer_reading ?? ""),
            fuel_litres: String(l.fuel_litres ?? ""),
            rate_per_litre: l.rate_per_litre != null ? String(l.rate_per_litre) : "",
            remarks: l.remarks || "",
        });
        setEditing(l.id);
        setOpen(true);
    };

    const save = async () => {
        if (!form.vehicle_id) { toast.error("Pick a vehicle"); return; }
        if (!form.refuel_date) { toast.error("Pick a refuel date"); return; }
        if (!Number.isFinite(newOdoNum) || newOdoNum <= 0) { toast.error("Odometer reading required"); return; }
        if (!Number.isFinite(fuelNum) || fuelNum <= 0) { toast.error("Fuel litres must be positive"); return; }
        if (form.rate_per_litre !== "" && (!Number.isFinite(rateNum) || rateNum < 0)) {
            toast.error("Rate per litre must be a non-negative number"); return;
        }
        setSubmitting(true);
        try {
            const payload = {
                vehicle_id: form.vehicle_id,
                driver_id: form.driver_id || null,
                refuel_date: form.refuel_date,
                odometer_reading: newOdoNum,
                fuel_litres: fuelNum,
                rate_per_litre: form.rate_per_litre === "" ? null : rateNum,
                remarks: form.remarks?.trim() || null,
            };
            if (editing) await api.put(`/mileage/${editing}`, payload);
            else await api.post("/mileage", payload);
            toast.success(editing ? "Mileage log updated" : "Mileage log added");
            setOpen(false); setForm(empty); setEditing(null);
            loadPage(1);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
        } finally {
            setSubmitting(false);
        }
    };

    const del = async (l) => {
        const label = `mileage log for ${l.vehicle_reg_number || l.vehicle_id} (${l.refuel_date})`;
        const r = await requestOrDelete("mileage_log", l.id, label, user?.role);
        if (r.ok) loadPage(page);
    };

    const exportCsv = async () => {
        const rows = await loadAll();
        const columns = [
            ["REFUEL DATE", (l) => l.__isTotal ? "TOTAL" : formatDate(l.refuel_date)],
            ["VEHICLE", (l) => l.__isTotal ? `${rows.length} log(s)` : (l.vehicle_reg_number || "")],
            ["DRIVER", (l) => l.__isTotal ? "" : (l.driver_name || "")],
            ["PREV ODO (KM)", (l) => l.__isTotal ? "" : (l.previous_odometer ?? "")],
            ["NEW ODO (KM)", (l) => l.__isTotal ? "" : (l.odometer_reading ?? "")],
            ["DISTANCE (KM)", (l) => l.distance_km],
            ["FUEL (L)", (l) => l.fuel_litres],
            ["RATE (₹/L)", (l) => l.__isTotal ? "" : (l.rate_per_litre ?? "")],
            ["COST (₹)", (l) => l.fuel_cost_inr ?? ""],
            ["FIXED MILEAGE (KMPL)", (l) => l.__isTotal ? "" : (l.fixed_mileage_kmpl ?? "")],
            ["OBTAINED MILEAGE (KMPL)", (l) => l.__isTotal ? "" : (l.obtained_mileage_kmpl ?? "")],
            ["EXPECTED FUEL (L)", (l) => l.__isTotal ? "" : (l.expected_fuel_litres ?? "")],
            ["FUEL Δ (L)", (l) => l.fuel_delta_litres ?? ""],
            ["AMOUNT GAIN/LOSS (₹)", (l) => l.amount_delta_inr ?? ""],
            ["REMARKS", (l) => l.__isTotal ? "" : (l.remarks || "")],
        ];
        const t = totals || rows.reduce((acc, l) => {
            acc.distance_km += Number(l.distance_km || 0);
            acc.fuel_litres += Number(l.fuel_litres || 0);
            if (l.fuel_cost_inr != null) acc.fuel_cost_inr = (acc.fuel_cost_inr || 0) + Number(l.fuel_cost_inr);
            if (l.fuel_delta_litres != null) acc.fuel_delta_litres = (acc.fuel_delta_litres || 0) + Number(l.fuel_delta_litres);
            if (l.amount_delta_inr != null) acc.amount_delta_inr = (acc.amount_delta_inr || 0) + Number(l.amount_delta_inr);
            return acc;
        }, { distance_km: 0, fuel_litres: 0 });
        const totalsRow = { __isTotal: true, ...t };
        downloadCsv(`mileage-${new Date().toISOString().slice(0, 10)}.csv`, [...rows, totalsRow], columns);
    };
    const exportPdf = async () => {
        await loadAll();
        printElement(tableRef.current, `EAZYFLEET · Mileage Calculation · ${total} record(s)`);
    };

    return (
        <div className="p-6 lg:p-8 animate-fade-in" data-testid="mileage-calc-page">
            <PageHeader
                eyebrow="FLEET OPERATIONS"
                title="Mileage calculation"
                subtitle="Log every refuelling event and compare obtained vs. rated mileage."
                action={<NewButton onClick={openAdd} label="Add refuel log" testid="add-mileage-btn" />}
            />

            <Surface className="overflow-hidden">
                <div className="px-5 py-3 border-b border-fleet-border flex items-center gap-3 flex-wrap">
                    <div className="relative flex-1 min-w-[220px] max-w-md">
                        <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-fleet-inkMuted" />
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search reg #, driver name, remarks…"
                            className="w-full pl-9 pr-3 py-2 text-sm border border-fleet-border rounded-md focus:outline-none focus:border-fleet-ink"
                            data-testid="mileage-search"
                        />
                    </div>
                    <select value={filterVehicle} onChange={(e) => setFilterVehicle(e.target.value)}
                        className="px-3 py-2 text-sm border border-fleet-border rounded-md bg-white focus:outline-none focus:border-fleet-ink min-w-[140px]"
                        data-testid="mileage-filter-vehicle">
                        <option value="">All vehicles</option>
                        {vehicles.map((v) => <option key={v.id} value={v.id}>{v.reg_number}</option>)}
                    </select>
                    <select value={filterDriver} onChange={(e) => setFilterDriver(e.target.value)}
                        className="px-3 py-2 text-sm border border-fleet-border rounded-md bg-white focus:outline-none focus:border-fleet-ink min-w-[140px]"
                        data-testid="mileage-filter-driver">
                        <option value="">All drivers</option>
                        {drivers.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                    </select>
                    <div className="inline-flex items-center gap-1">
                        <span className="label-mono">FROM</span>
                        <input type="date" value={dateFrom}
                            onChange={(e) => setDateFrom(e.target.value)}
                            className="px-2 py-1.5 text-xs border border-fleet-border rounded-md bg-white focus:outline-none focus:border-fleet-ink font-mono"
                            data-testid="mileage-filter-date-from" />
                        <span className="label-mono ml-1">TO</span>
                        <input type="date" value={dateTo}
                            onChange={(e) => setDateTo(e.target.value)}
                            className="px-2 py-1.5 text-xs border border-fleet-border rounded-md bg-white focus:outline-none focus:border-fleet-ink font-mono"
                            data-testid="mileage-filter-date-to" />
                    </div>
                    {hasFilters && (
                        <button onClick={clearFilters} className="text-xs text-fleet-inkSoft hover:text-fleet-ink underline flex items-center gap-1" data-testid="mileage-clear-filters">
                            <X size={12} /> Clear
                        </button>
                    )}
                </div>
                <div className="px-5 py-2 border-b border-fleet-border">
                    <ReportToolbar
                        page={page}
                        totalPages={totalPages}
                        total={total}
                        pageSize={PAGE_SIZE}
                        onPageChange={(p) => { setPage(p); loadPage(p); }}
                        onRefresh={() => loadPage(page)}
                        zoom={zoom}
                        onZoomChange={setZoom}
                        onExportCsv={exportCsv}
                        onExportPdf={exportPdf}
                        busy={busy}
                    />
                </div>
                <div className="overflow-x-auto">
                    <ZoomScope zoom={zoom}>
                    <table className="w-full text-sm" ref={tableRef}>
                        <thead className="bg-slate-50"><tr className="text-left">
                            {[
                                "DATE", "VEHICLE", "DRIVER", "PREV ODO", "NEW ODO",
                                "DISTANCE", "FUEL (L)", "RATE (₹/L)", "COST (₹)",
                                "FIXED (KMPL)", "OBTAINED (KMPL)",
                                "FUEL Δ (L)", "AMT GAIN/LOSS (₹)", "REMARKS", "",
                            ].map((h, i) => (
                                <th key={h} className={`label-mono px-4 py-3 font-normal whitespace-nowrap${i === 14 ? " no-print" : ""}`}>{h}</th>
                            ))}
                        </tr></thead>
                        <tbody>
                            {logs.map((l) => {
                                const obtained = l.obtained_mileage_kmpl;
                                const fixed = l.fixed_mileage_kmpl;
                                const obtainedTone = obtained == null || fixed == null
                                    ? "text-fleet-inkSoft"
                                    : obtained >= fixed ? "text-emerald-700"
                                        : obtained >= fixed * 0.85 ? "text-amber-700"
                                            : "text-rose-700";
                                const delta = l.fuel_delta_litres;
                                const deltaTone = delta == null ? "text-fleet-inkSoft"
                                    : delta > 0.5 ? "text-rose-700"
                                        : delta < -0.5 ? "text-emerald-700"
                                            : "text-fleet-inkSoft";
                                const amt = l.amount_delta_inr;
                                const amtTone = amt == null ? "text-fleet-inkSoft"
                                    : amt > 0 ? "text-rose-700"
                                        : amt < 0 ? "text-emerald-700"
                                            : "text-fleet-inkSoft";
                                return (
                                    <tr key={l.id} className="border-t border-fleet-border data-grid-row">
                                        <td className="px-4 py-3 font-mono text-xs whitespace-nowrap">{formatDate(l.refuel_date)}</td>
                                        <td className="px-4 py-3 font-mono font-medium whitespace-nowrap">{l.vehicle_reg_number || "—"}</td>
                                        <td className="px-4 py-3 whitespace-nowrap">{l.driver_name || <span className="text-fleet-inkSoft">—</span>}</td>
                                        <td className="px-4 py-3 font-mono">{(l.previous_odometer ?? 0).toLocaleString()}</td>
                                        <td className="px-4 py-3 font-mono">{(l.odometer_reading ?? 0).toLocaleString()}</td>
                                        <td className="px-4 py-3 font-mono">{(l.distance_km ?? 0).toLocaleString()}</td>
                                        <td className="px-4 py-3 font-mono">{(l.fuel_litres ?? 0).toFixed(2)}</td>
                                        <td className="px-4 py-3 font-mono text-fleet-inkSoft">{l.rate_per_litre != null ? `₹${Number(l.rate_per_litre).toFixed(2)}` : "—"}</td>
                                        <td className="px-4 py-3 font-mono">{l.fuel_cost_inr != null ? `₹${Number(l.fuel_cost_inr).toFixed(2)}` : "—"}</td>
                                        <td className="px-4 py-3 font-mono text-fleet-inkSoft">{fixed != null ? Number(fixed).toFixed(2) : "—"}</td>
                                        <td className={`px-4 py-3 font-mono font-semibold ${obtainedTone}`}>{obtained != null ? Number(obtained).toFixed(2) : "—"}</td>
                                        <td className={`px-4 py-3 font-mono ${deltaTone}`} title={delta != null ? (delta > 0 ? "extra fuel consumed (low mileage)" : "fuel saved (good mileage)") : ""}>
                                            {delta != null ? (delta > 0 ? `+${Number(delta).toFixed(2)}` : Number(delta).toFixed(2)) : "—"}
                                        </td>
                                        <td className={`px-4 py-3 font-mono font-semibold ${amtTone}`} title={amt != null ? (amt > 0 ? "monetary LOSS" : amt < 0 ? "monetary GAIN" : "neutral") : ""}>
                                            {amt != null ? `${amt > 0 ? "-₹" : amt < 0 ? "+₹" : "₹"}${Math.abs(amt).toFixed(2)}` : "—"}
                                        </td>
                                        <td className="px-4 py-3 text-xs text-fleet-inkSoft max-w-[260px]">
                                            <div className="whitespace-pre-wrap break-words" data-testid={`mileage-remarks-${l.id}`}>
                                                {l.remarks || <span className="text-fleet-inkMuted">—</span>}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-right whitespace-nowrap no-print">
                                            <Button variant="ghost" size="sm" onClick={() => openEdit(l)} data-testid={`mileage-edit-${l.id}`}>Edit</Button>
                                            <Button variant="ghost" size="sm" onClick={() => del(l)} data-testid={`mileage-del-${l.id}`}>Del</Button>
                                        </td>
                                    </tr>
                                );
                            })}
                            {logs.length > 0 && totals && (
                                <tr className="border-t-2 border-fleet-ink bg-slate-50 font-semibold" data-testid="mileage-totals-row">
                                    <td className="px-4 py-3 label-mono text-fleet-ink">TOTAL</td>
                                    <td className="px-4 py-3 text-xs text-fleet-inkSoft" colSpan={4}>{total} log(s) in selection</td>
                                    <td className="px-4 py-3 font-mono">{totals.distance_km.toLocaleString()}</td>
                                    <td className="px-4 py-3 font-mono">{totals.fuel_litres.toFixed(2)}</td>
                                    <td className="px-4 py-3"></td>
                                    <td className="px-4 py-3 font-mono">{totals.fuel_cost_inr != null ? `₹${totals.fuel_cost_inr.toFixed(2)}` : "—"}</td>
                                    <td className="px-4 py-3"></td>
                                    <td className="px-4 py-3"></td>
                                    <td className={`px-4 py-3 font-mono ${totals.fuel_delta_litres == null ? "text-fleet-inkSoft" : totals.fuel_delta_litres > 0 ? "text-rose-700" : "text-emerald-700"}`}>
                                        {totals.fuel_delta_litres != null ? (totals.fuel_delta_litres > 0 ? `+${totals.fuel_delta_litres.toFixed(2)}` : totals.fuel_delta_litres.toFixed(2)) : "—"}
                                    </td>
                                    <td className={`px-4 py-3 font-mono ${totals.amount_delta_inr == null ? "text-fleet-inkSoft" : totals.amount_delta_inr > 0 ? "text-rose-700" : "text-emerald-700"}`}>
                                        {totals.amount_delta_inr != null
                                            ? `${totals.amount_delta_inr > 0 ? "-₹" : totals.amount_delta_inr < 0 ? "+₹" : "₹"}${Math.abs(totals.amount_delta_inr).toFixed(2)}`
                                            : "—"}
                                    </td>
                                    <td className="px-4 py-3"></td>
                                    <td className="px-4 py-3 no-print"></td>
                                </tr>
                            )}
                            {logs.length > 0 && !totals && (
                                <tr className="border-t-2 border-fleet-ink bg-slate-50" data-testid="mileage-totals-pending">
                                    <td colSpan={15} className="px-4 py-2 text-xs text-fleet-inkSoft text-center">
                                        Totals for all {total} matching log(s) — click <strong>CSV</strong> / <strong>PDF</strong> to fetch &amp; compute, or browse pages to view individual rows.
                                    </td>
                                </tr>
                            )}
                            {logs.length === 0 && (
                                <tr><td colSpan={15} className="px-5 py-12 text-center text-sm text-fleet-inkSoft">
                                    {total === 0 ? "No mileage logs yet — click \"Add refuel log\"." : "No logs match the current filters."}
                                </td></tr>
                            )}
                        </tbody>
                    </table>
                    </ZoomScope>
                </div>
            </Surface>

            <Modal open={open} onClose={() => setOpen(false)}
                title={editing ? "Edit mileage log" : "Add mileage log"}
                size="lg"
                footer={<>
                    <Button variant="secondary" onClick={() => setOpen(false)}>Cancel</Button>
                    <Button onClick={save} disabled={submitting} data-testid="save-mileage">
                        <GasPump size={14} /> {submitting ? "Saving…" : "Save log"}
                    </Button>
                </>}>
                <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <VehicleSearchSelect
                            vehicles={vehicles}
                            value={form.vehicle_id}
                            onChange={(id) => setForm({ ...form, vehicle_id: id })}
                            label="Vehicle"
                            placeholder="Search by reg #…"
                            testid="mileage-vehicle-select"
                        />
                        <div>
                            <div className="label-mono mb-1.5">Driver</div>
                            <select
                                value={form.driver_id}
                                onChange={(e) => setForm({ ...form, driver_id: e.target.value })}
                                className="w-full px-3 py-2 text-sm border border-fleet-border rounded-md focus:outline-none focus:border-fleet-ink bg-white"
                                data-testid="mileage-driver-select"
                            >
                                <option value="">— Select driver (optional) —</option>
                                {drivers.map((d) => (
                                    <option key={d.id} value={d.id}>
                                        {d.name}{d.phone ? ` · ${d.phone}` : ""}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <Input
                            label="Date of refuelling"
                            type="date"
                            value={form.refuel_date}
                            onChange={(e) => setForm({ ...form, refuel_date: e.target.value })}
                            data-testid="mileage-refuel-date"
                        />
                        <Input
                            label="Quantity of fuel (litres)"
                            type="number"
                            step="0.01"
                            min="0"
                            value={form.fuel_litres}
                            onChange={(e) => setForm({ ...form, fuel_litres: e.target.value })}
                            placeholder="e.g. 45.50"
                            data-testid="mileage-fuel-litres"
                        />
                        <Input
                            label="Rate / litre (₹)"
                            type="number"
                            step="0.01"
                            min="0"
                            value={form.rate_per_litre}
                            onChange={(e) => setForm({ ...form, rate_per_litre: e.target.value })}
                            placeholder="e.g. 96.50"
                            data-testid="mileage-rate-per-litre"
                        />
                        <div>
                            <div className="label-mono mb-1.5">Previous odometer (km)</div>
                            <div className="px-3 py-2 rounded-md border bg-slate-50 border-fleet-border text-sm font-mono">
                                {selectedVehicle
                                    ? `${(prevOdoForSelection ?? 0).toLocaleString()} km`
                                    : "—"}
                            </div>
                        </div>
                        <Input
                            label="New odometer reading (km)"
                            type="number"
                            step="1"
                            min={prevOdoForSelection != null ? prevOdoForSelection + 1 : 1}
                            value={form.odometer_reading}
                            onChange={(e) => setForm({ ...form, odometer_reading: e.target.value.replace(/[^\d.]/g, "") })}
                            placeholder={selectedVehicle ? `Must be greater than ${(prevOdoForSelection ?? 0).toLocaleString()}` : "Pick a vehicle first"}
                            disabled={!selectedVehicle}
                            data-testid="mileage-new-odo"
                        />
                    </div>

                    <div>
                        <div className="label-mono mb-1.5">Remarks (reason for low mileage, etc.)</div>
                        <textarea
                            value={form.remarks}
                            onChange={(e) => setForm({ ...form, remarks: e.target.value })}
                            rows={3}
                            placeholder="Heavy traffic, ac usage, hilly terrain, idling, etc."
                            className="w-full px-3 py-2 text-sm border border-fleet-border rounded-md focus:outline-none focus:border-fleet-ink resize-y whitespace-pre-wrap"
                            data-testid="mileage-remarks"
                        />
                    </div>

                    {/* Live preview */}
                    {selectedVehicle && distancePreview != null && (
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 p-3 border border-fleet-border rounded-md bg-slate-50/60">
                            {[
                                ["DISTANCE", `${distancePreview.toLocaleString()} km`],
                                ["FIXED", fixedKmpl != null ? `${fixedKmpl} kmpl` : "—"],
                                ["OBTAINED", obtainedPreview != null ? `${obtainedPreview} kmpl` : "—"],
                                ["FUEL Δ", fuelDeltaPreview != null ? `${fuelDeltaPreview > 0 ? "+" : ""}${fuelDeltaPreview} L` : "—"],
                                ["COST", totalCostPreview != null ? `₹${totalCostPreview.toFixed(2)}` : "—"],
                                ["AMT GAIN/LOSS", amountDeltaPreview != null
                                    ? `${amountDeltaPreview > 0 ? "-₹" : amountDeltaPreview < 0 ? "+₹" : "₹"}${Math.abs(amountDeltaPreview).toFixed(2)}`
                                    : "—"],
                            ].map(([k, v]) => (
                                <div key={k} className="px-2 py-1.5">
                                    <div className="label-mono">{k}</div>
                                    <div className="font-mono text-sm">{v}</div>
                                </div>
                            ))}
                        </div>
                    )}
                    {selectedVehicle && !fixedKmpl && (
                        <div className="text-xs text-fleet-warning">
                            <Badge variant="warning">No fixed mileage set</Badge>
                            <span className="ml-2">Set <strong>Fixed mileage (km/L)</strong> on the vehicle record to enable expected-fuel / delta analysis.</span>
                        </div>
                    )}
                </div>
            </Modal>
        </div>
    );
}
